import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import { fadeUp, stagger, viewportOpts } from '../hooks/useScrollAnimation';
import { facilities, safetyCommitments } from '../data/facilities';
import * as Icons from 'lucide-react';

const Wave = ({ fill, bg = "transparent" }) => (
  <div className="wave-divider" style={{ background: bg }}>
    <svg viewBox="0 0 1440 70" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: 50 }}>
      <path d="M0,35 C200,70 400,0 720,35 C1040,70 1280,5 1440,35 L1440,70 L0,70 Z" fill={fill} />
    </svg>
  </div>
);

const DynamicIcon = ({ name, fallback = "Sparkles", className, size }) => {
  const IconComponent = Icons[name] || Icons[fallback];
  return <IconComponent className={className} size={size} />;
};

export default function Facilities() {
  return (
    <div>
      <PageHero 
        title="Safe, Colorful and Child-Friendly Learning Spaces" 
        subtitle="Every part of our preschool environment is designed to support safety, comfort, exploration, creativity, movement, and joyful learning." 
        breadcrumb="Facilities" 
      />

      {/* Facilities Cards Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Infrastructure</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Our Campus Facilities</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Explore the spaces designed intentionally for safety, hygiene, play, and interactive learning.</p>
          </motion.div>

          <motion.div 
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {facilities.map((fac, i) => (
              <motion.div 
                key={i} 
                variants={fadeUp} 
                className="cartoon-card bg-white rounded-3xl p-8 border border-gray-100 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: fac.color }}>
                    <DynamicIcon name={fac.iconName} fallback="Sparkles" size={26} style={{ color: fac.iconColor }} />
                  </div>
                  <h3 className="font-nunito font-bold text-xl text-[#1A1A1A] mb-3">{fac.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{fac.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Wave fill="#F8FAFC" bg="white" />

      {/* Safety Commitment Section */}
      <section className="py-20 bg-[#F8FAFC] polka-dots">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            
            {/* Visual Callout */}
            <motion.div 
              className="lg:col-span-5 bg-[#FDE8E9] rounded-3rem p-10 border border-[#C8161D]/20 text-center"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewportOpts}
              transition={{ duration: 0.6 }}
            >
              <div className="w-20 h-20 bg-[#C8161D] text-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <ShieldCheck size={40} />
              </div>
              <h3 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mb-4">Our Safety Commitment</h3>
              <p className="text-gray-600 leading-relaxed text-sm mb-4">
                Nothing is more important than your child's physical and emotional safety. We maintain absolute vigilance and protocol to make sure Happykidz remains a secure haven.
              </p>
              <span className="text-[#C8161D] font-nunito font-bold text-sm">CCTV-monitored &amp; ISO-Quality Certified</span>
            </motion.div>

            {/* List */}
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewportOpts}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label mb-4 inline-block">Security Protocols</span>
              <h2 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mb-6">How We Ensure Safety</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {safetyCommitments.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                    <CheckCircle2 className="text-[#9DC41E] flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
}
